import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import AIChatWidget from "./AIChatWidget";
import {
  LayoutDashboard,
  Package,
  Upload,
  Trash2,
  Zap,
  FileText,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronRight,
  ChevronDown,
  Building2,
  Phone,
  MessageCircle,
  BarChart3,
  Lightbulb,
  Activity,
  Repeat,
  Gauge,
  QrCode,
  CalendarRange,
  FileDown,
  Plug,
  CreditCard,
  Rocket,
  HeartPulse,
  Crown,
  ScanLine,
  Brain,
  Wallet,
  Film
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png";

/* ─── Sidebar groups (collapsible like LogLayout) ─── */
const pharmacySidebarGroups = [
  {
    id: "main",
    items: [
      { path: "/dashboard", icon: LayoutDashboard, label: "Tableau de Bord" },
      { path: "/monthly", icon: CalendarRange, label: "Suivi Mensuel" },
    ],
  },
  {
    id: "produits",
    label: "Produits & Import",
    icon: Package,
    items: [
      { path: "/products", icon: Package, label: "Produits" },
      { path: "/scanner", icon: ScanLine, label: "Scanner IA" },
      { path: "/stock-intelligence", icon: Brain, label: "Alertes Stock IA" },
      { path: "/import", icon: Upload, label: "Import" },
      { path: "/integration", icon: Plug, label: "API Caisse" },
    ],
  },
  {
    id: "destructions",
    label: "Destructions & Declarations",
    icon: Trash2,
    items: [
      { path: "/destructions", icon: Trash2, label: "Destructions" },
      { path: "/destruction-express", icon: Zap, label: "Destruction Express" },
      { path: "/déclaration-rapide", icon: Phone, label: "Declaration Mobile" },
      { path: "/qrcode", icon: QrCode, label: "QR Codes" },
    ],
  },
  {
    id: "rapports",
    label: "Rapports & Analyses",
    icon: BarChart3,
    items: [
      { path: "/reports", icon: FileText, label: "Rapports" },
      { path: "/analytics", icon: BarChart3, label: "Analyse" },
      { path: "/recommendations", icon: Lightbulb, label: "Conseils" },
      { path: "/sentinel", icon: Activity, label: "Sentinel" },
      { path: "/itpp", icon: Gauge, label: "Indicateurs ITPP" },
    ],
  },
  {
    id: "general",
    items: [
      { path: "/exchange", icon: Repeat, label: "PharmExchange" },
    ],
  },
];

const adminExtraGroups = [
  {
    id: "admin",
    items: [
      { path: "/admin", icon: Shield, label: "Admin CMES" },
      { path: "/downloads", icon: FileDown, label: "Documents" },
      { path: "/deployment", icon: Rocket, label: "Mise en Production" },
      { path: "/system-status", icon: HeartPulse, label: "Statut Systeme" },
    ],
  },
];

const superAdminExtraGroups = [
  {
    id: "superadmin",
    label: "Direction CMES",
    icon: Crown,
    items: [
      { path: "/superadmin", icon: Shield, label: "Super Admin", superadmin: true },
      { path: "/superadmin/gestion", icon: Wallet, label: "Gestion Interne", superadmin: true },
    ],
  },
  {
    id: "superadmin-billing",
    label: "Abonnements & Facturation",
    icon: CreditCard,
    items: [
      { path: "/superadmin?tab=subscriptions", icon: Crown, label: "Gestion Abonnements" },
      { path: "/superadmin?tab=payments", icon: Wallet, label: "Suivi Paiements" },
    ],
  },
  {
    id: "superadmin-content",
    label: "Contenu & Communication",
    icon: FileText,
    items: [
      { path: "/superadmin/training-videos", icon: Film, label: "Vidéos commerciales" },
      { path: "/superadmin/analytics-vitrine", icon: BarChart3, label: "Analytics Vitrine" },
    ],
  },
  {
    id: "superadmin-legacy",
    label: "Admin classique",
    icon: LayoutDashboard,
    items: [
      { path: "/admin", icon: LayoutDashboard, label: "Admin CMES" },
    ],
  },
];

function getSidebarGroups(role) {
  if (role === "superadmin") {
    return [...superAdminExtraGroups, ...pharmacySidebarGroups, ...adminExtraGroups.map(g => ({ ...g, items: g.items.filter(i => i.path !== "/admin") }))];
  }
  if (role === "admin" || role === "logistics") {
    return [{ id: "admin-top", items: [{ path: "/admin", icon: Shield, label: "Admin CMES" }] }, ...pharmacySidebarGroups, ...adminExtraGroups.map(g => ({ ...g, items: g.items.filter(i => i.path !== "/admin") }))];
  }
  return pharmacySidebarGroups;
}export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sidebarGroups = getSidebarGroups(user?.role);

  // Auto-open the group containing the current path
  const [openGroups, setOpenGroups] = useState(() => {
    const active = sidebarGroups.find(g => g.items?.some(i => location.pathname === i.path));
    const defaults = ["main", "general", "superadmin", "admin-top", "admin"];
    if (active && !defaults.includes(active.id)) defaults.push(active.id);
    return defaults;
  });

  const toggleGroup = (id) => {
    setOpenGroups(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  /* ─── Render a single nav link ─── */
  const renderNavLink = (item, closeMobile) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={closeMobile ? () => setSidebarOpen(false) : undefined}
        data-testid={`nav-${item.path.replace(/\//g, "-").slice(1) || "home"}`}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
          isActive
            ? "bg-cyan-500/15 text-cyan-400 font-semibold border-l-2 border-cyan-500"
            : item.superadmin ? "text-amber-400/80 hover:bg-amber-500/10 hover:text-amber-400" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{item.label}</span>
      </Link>
    );
  };

  /* ─── Render sidebar groups (shared between desktop & mobile) ─── */
  const renderGroups = (closeMobile = false) => (
    sidebarGroups.map((group) => {
      if (!group.items || group.items.length === 0) return null;
      const isOpen = openGroups.includes(group.id);
      const hasActiveChild = group.items.some(i => location.pathname === i.path);

      // No label = ungrouped, render items directly
      if (!group.label) {
        return group.items.map(item => renderNavLink(item, closeMobile));
      }

      // Grouped with collapsible header
      const GroupIcon = group.icon;
      return (
        <div key={group.id} className="mt-1">
          <button
            onClick={() => toggleGroup(group.id)}
            data-testid={`group-${group.id}`}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
              hasActiveChild ? "text-cyan-400" : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <GroupIcon className="w-3.5 h-3.5" />
            <span className="flex-1 text-left">{group.label}</span>
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {isOpen && (
            <div className="ml-2 space-y-0.5 mt-0.5">
              {group.items.map(item => renderNavLink(item, closeMobile))}
            </div>
          )}
        </div>
      );
    })
  );

  return (
    <div className="min-h-screen flex" style={{background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)'}}>
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-60 cmes-sidebar backdrop-blur-md border-r border-slate-200/50 fixed h-full z-50" style={{backgroundColor: '#0f172a'}}>
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-white/5" style={{backgroundColor: '#0f172a'}}>
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CMES" className="h-12 w-auto" />
            <div>
              <span className="font-outfit font-bold text-cyan-400 text-lg">CMES</span>
              <span className="block text-[10px] text-amber-400 tracking-wider font-semibold">PHARMA INTEL</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {renderGroups(false)}
        </nav>

        {/* Quick Contact */}
        <div className="p-4 border-t border-white/5 bg-amber-500/5">
          <p className="text-xs text-amber-400 font-semibold mb-2">URGENCE DESTRUCTION</p>
          <div className="flex gap-2">
            <a href="tel:+2250700000000" className="flex-1 flex items-center justify-center gap-1 bg-cyan-600 text-white py-2 rounded-lg text-xs font-medium hover:bg-cyan-700 transition-colors">
              <Phone className="w-3 h-3" /> Appeler
            </a>
            <a href="https://wa.me/2250700000000" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white py-2 rounded-lg text-xs font-medium hover:bg-green-700 transition-colors">
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </a>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-white/5" style={{backgroundColor: '#0f172a'}}>
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-10 w-10 border-2 border-cyan-500/30">
              <AvatarFallback className="bg-cyan-500/10 text-cyan-400 font-semibold">
                {user?.full_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.pharmacy_name || user?.role}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-slate-500 hover:text-red-400 hover:bg-red-500/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 cmes-sidebar backdrop-blur-md border-b border-slate-200/50 z-50 flex items-center justify-between px-4" style={{backgroundColor: '#0f172a'}}>
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="CMES" className="h-10 w-auto" />
          <span className="font-outfit font-bold text-cyan-400">CMES</span>
        </Link>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-slate-400">
                <Avatar className="h-8 w-8 border border-cyan-500/30">
                  <AvatarFallback className="bg-cyan-500/10 text-cyan-400 text-xs font-semibold">
                    {user?.full_name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" style={{backgroundColor: '#0f172a', borderColor: 'rgba(255,255,255,0.1)'}}>
              <div className="px-3 py-2">
                <p className="text-sm font-medium" style={{color: '#f8fafc'}}>{user?.full_name}</p>
                <p className="text-xs" style={{color: '#64748b'}}>{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-300 focus:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" /> Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 cmes-sidebar backdrop-blur-md pt-16" style={{backgroundColor: 'rgba(15,23,42,0.98)'}}>
          <nav className="p-4 space-y-0.5 overflow-y-auto" style={{ maxHeight: "calc(100vh - 8rem)" }}>
            {renderGroups(true)}
          </nav>
          <div className="p-4 border-t border-white/5 mx-4 mt-4 bg-amber-500/5 rounded-xl">
            <p className="text-xs text-amber-400 font-semibold mb-2">URGENCE DESTRUCTION</p>
            <div className="flex gap-2">
              <a href="tel:+2250700000000" className="flex-1 flex items-center justify-center gap-1 bg-cyan-600 text-white py-3 rounded-lg text-sm font-medium">
                <Phone className="w-4 h-4" /> Appeler
              </a>
              <a href="https://wa.me/2250700000000" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white py-3 rounded-lg text-sm font-medium">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-60 pt-16 md:pt-0 min-h-screen">
        {children}
      </main>
      <AIChatWidget />
    </div>
  );
};

export default Layout;
